import { CdkDragDrop, DragDropModule, moveItemInArray, transferArrayItem } from "@angular/cdk/drag-drop";
import { CommonModule } from "@angular/common";
import { AfterViewInit, Component, inject, ChangeDetectionStrategy } from "@angular/core";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatIconModule } from "@angular/material/icon";
import { RouterModule } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { DialogService, DialogTemplate } from "src/app/components/global/dialog/dialog.service";
import { BookmarkEditComponent, BookmarkEditData } from "src/app/components/global/dialog/impl/bookmark-edit/bookmark-edit.component";
import {
  BookmarkGroupEditComponent,
  BookmarkGroupEditData,
} from "src/app/components/global/dialog/impl/bookmark-group-edit/bookmark-group-edit.component";
import { DefaultComponent } from "src/app/default.component";
import { Bookmark, BookmarkGroup } from "src/interfaces/session/session-bookmark";
import { ApplicationService } from "src/services/application.service";
import { BookmarkService } from "./bookmarks.service";

@Component({
  imports: [CommonModule, MatExpansionModule, MatIconModule, DragDropModule, RouterModule, TranslateModule],
  selector: "app-bookmarks",
  templateUrl: "./bookmarks.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./bookmarks.component.less",
})
export class BookmarkScreenComponent extends DefaultComponent implements AfterViewInit {
  public application: ApplicationService;
  public bookmarks: BookmarkService;
  public dialog: DialogService;

  private translate: TranslateService;

  public constructor() {
    super();
    this.application = inject(ApplicationService);
    this.bookmarks = inject(BookmarkService);
    this.dialog = inject(DialogService);
    this.translate = inject(TranslateService);
  }

  public async ngAfterViewInit(): Promise<void> {
    await this.bookmarks.initialize();
    this.application.title.next(this.translate.instant("BOOKMARKS.TITLE"));
    this.application.context.next([]);
    this.application.loading.next(false);
  }

  public drop(event: CdkDragDrop<BookmarkGroup>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data.bookmarks, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data.bookmarks, event.container.data.bookmarks, event.previousIndex, event.currentIndex);

      if (event.previousContainer.data.bookmarks.length < 1) {
        this.deleteGroup(event.previousContainer.data);
      }
    }

    this.bookmarks.saveBookmarks();
  }

  public deleteBookmark(bookmark: Bookmark): void {
    const bookmarkGroup = Array.from(this.bookmarks.bookmarkGroup$.value.values()).find((g) => g.bookmarks.some((x) => x.link === bookmark.link));
    if (bookmarkGroup) {
      const bookmarks = bookmarkGroup.bookmarks.filter((bookmarkCompare) => bookmarkCompare.link !== bookmark.link);

      bookmarkGroup.bookmarks = bookmarks;
      this.bookmarks.saveBookmarks();
    } else {
      throw new Error("Bookmarkgroup not found");
    }
  }

  public deleteGroup(bookmarkGroup: BookmarkGroup): void {
    this.bookmarks.removeBookmarkGroup(bookmarkGroup);
    this.bookmarks.saveBookmarks();
  }

  public editBookmark(bookmark: Bookmark): void {
    this.dialog.open<BookmarkEditData>(
      BookmarkEditComponent,
      {
        title: { label: "DIALOG.BOOKMARKS.EDIT" },
        bookmark,
      },
      DialogTemplate.MODAL,
    );
  }

  public editGroup(bookmarkGroup: BookmarkGroup): void {
    this.dialog.open<BookmarkGroupEditData>(
      BookmarkGroupEditComponent,
      {
        title: { label: "DIALOG.BOOKMARKS.EDIT_GROUP" },
        bookmarkGroup,
      },
      DialogTemplate.MODAL,
    );
  }
}
